/**
 * Aviso modo demo — flotante, mismo estilo ámbar, con detalle expandible.
 * Se cierra con X en la ruta actual y vuelve al cambiar de sección.
 */

import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Sparkles, X, ChevronDown } from "lucide-react";
import { useAuthStore } from "@/store/authStore";
import { isDemoSession } from "@/lib/demo/startDemoSession";

export default function DemoModeBanner() {
  const token = useAuthStore((s) => s.token);
  const location = useLocation();
  const [dismissedPath, setDismissedPath] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setDismissedPath(null);
    setExpanded(false);
  }, [location.pathname]);

  if (!isDemoSession(token)) return null;
  if (dismissedPath === location.pathname) return null;

  return (
    <div
      className="pointer-events-none absolute inset-x-2 top-2 z-[55] max-lg:top-[3.5rem] sm:inset-x-3 lg:top-2.5"
      role="status"
      aria-live="polite"
    >
      <div className="pointer-events-auto mx-auto max-w-3xl overflow-hidden rounded-lg border border-amber-200/90 bg-amber-50/95 shadow-md backdrop-blur-[2px] dark:border-amber-800/60 dark:bg-amber-950/95">
        <div className="flex items-center gap-1.5 px-2 py-1.5 sm:gap-2 sm:px-2.5 sm:py-1.5">
          <Sparkles
            className="h-3 w-3 shrink-0 text-amber-600 dark:text-amber-400"
            aria-hidden
          />
          <p className="min-w-0 flex-1 text-[9px] leading-tight text-amber-900 dark:text-amber-100 sm:text-[10px]">
            <strong className="font-bold">Modo demo</strong>
            <span className="text-amber-800/90 dark:text-amber-100/90">
              {" "}
              — Los datos viven en tu dispositivo, no en la nube. El espacio
              local es limitado.
            </span>
          </p>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="shrink-0 rounded p-0.5 text-amber-700 hover:bg-amber-100 dark:text-amber-200 dark:hover:bg-amber-900/50"
            aria-expanded={expanded}
            aria-label={
              expanded
                ? "Ocultar explicación del modo demo"
                : "Ver por qué funciona así el modo demo"
            }
            title={expanded ? "Ocultar detalle" : "¿Por qué es así?"}
          >
            <ChevronDown
              className={`h-3.5 w-3.5 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
              aria-hidden
            />
          </button>
          {!expanded ? (
            <Link
              to="/register"
              className="shrink-0 text-[9px] font-bold text-primary-700 underline underline-offset-2 hover:text-primary-800 dark:text-primary-300 sm:text-[10px]"
            >
              Regístrate aquí
            </Link>
          ) : null}
          <button
            type="button"
            onClick={() => setDismissedPath(location.pathname)}
            className="shrink-0 rounded p-0.5 text-amber-700/80 hover:bg-amber-100 dark:text-amber-200 dark:hover:bg-amber-900/50"
            aria-label="Cerrar aviso"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>

        {expanded ? (
          <div className="border-t border-amber-200/80 px-2.5 py-2 dark:border-amber-800/50 sm:px-3 sm:py-2.5">
            <p className="text-[9px] font-semibold leading-snug text-amber-950 dark:text-amber-50 sm:text-[10px]">
              Prueba la experiencia real completa — como si ya hubieras pagado.
            </p>
            <ul className="mt-1.5 space-y-1 text-[8.5px] leading-snug text-amber-900/95 dark:text-amber-100/90 sm:text-[9.5px]">
              <li>
                <strong>Sin registro ni espera:</strong> usa todo el sistema
                ahora, sin los 7 días de prueba.
              </li>
              <li>
                <strong>Solo en tu dispositivo</strong> (computador, celular o
                tablet): nadie más puede ver tu contenido; es privado para ti.
              </li>
              <li>
                <strong>Así la demo es gratis:</strong> al no conectarse a una
                base de datos en la nube, reducimos costos y tú pruebas sin
                pagar.
              </li>
              <li>
                <strong>
                  Tus datos se guardan solo aquí, y el espacio local no es
                  infinito (~5 MB).
                </strong>{" "}
                Con mucho uso (muchas ventas, fotos o documentos) el espacio
                puede llenarse. Te avisaremos cuando esté casi lleno y, desde{" "}
                <strong>Ajustes → Almacenamiento</strong>, podrás ver cuánto
                queda, <strong>descargar tu respaldo</strong> y liberar espacio
                sin perder tus datos.
              </li>
              <li>
                <strong>Es para practicar</strong> con un ejemplo real antes de
                comprar. Cuando quieras respaldo en la nube, varios usuarios y
                uso en producción,{" "}
                <Link
                  to="/register"
                  className="font-bold text-primary-700 underline underline-offset-2 hover:text-primary-800 dark:text-primary-300"
                >
                  regístrate aquí
                </Link>
                .
              </li>
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
}
